import React, { useEffect, useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { Button } from "@mui/material";
import {
    fetchOrganizationFilters,
    updateGeneralFilters,
    clearGeneralFilters,
    loadReportData,
    clearReportData,
} from "../../../../../actions";
import { useWebServer } from "../../../../../providers";
import { phraseDownloadDataFilter } from "../../../../../utils/dataDownload";
import {
    rawDataColumns,
    DEFAULT_ALG_CONFIDENCE_THRESHOLDS,
    REPORTS_TYPES,
} from "../../../../../utils/const";
import { useLocation } from "react-router-dom";
import ReportFilter from "../ReportFilter";
import { handleDownloadChart, handleDownloadReport } from './helpers'
import ReportsLayout from "./ReportsLayout";
import LoadingCircularProgress from './LoadingCircularProgress'


const CLICKED_BUTTON_MAP = {
    "Raw-Data": REPORTS_TYPES.RAW_DATA,
    "Species-Summary-Among-Collections": REPORTS_TYPES.ABUNDANCE,
    "Weekly-Species-report": REPORTS_TYPES.WEEKLY_REPORT,
    "Pie-chart": REPORTS_TYPES.PIE_CHART,
    "Bar-chart": REPORTS_TYPES.ABUNDANCE,
    "LineGraph-Chart": REPORTS_TYPES.LINE_CHART,
};

const Reports = ({
    fetchOrganizationFilters,
    timeZone,
    generalFilters,
    loadReportData,
    clearReportData,
    data,
    loading,
}) => {
    const [isDropdownVisible, setIsDropdownVisible] = useState(true);

    const {
        sendRequest,
        downloadOrganizationDataWithAbundance,
        downloadOrganizationDataWithWeekly,
        downloadSpeciesLineChart,
        downloadOrganizationDataWithRawData,
        getOrganizationSummaryData
    } = useWebServer();
    const [showReport, setShowReport] = useState(false);
    const location = useLocation();
    const [clickedButton, setClickedButton] = useState(null);
    const [rawData, setRawData] = useState([]);
    const [readyDownload, setReadyDownload] = useState(false);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        rawDataColumns.reduce((json, value, key) => {
            json[value] = true;
            return json;
        }, {})
    );

    useEffect(() => {
        return () => {
            clearReportData();
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchOrganizationFilters(sendRequest);
    }, [fetchOrganizationFilters, sendRequest]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const buttonParam = searchParams.get("button");
        if (buttonParam) {
            setClickedButton(buttonParam);
        }
    }, [location.search]);

    useEffect(() => {
        setRawData(data);
        if (data.length > 0) {
            setReadyDownload(true);
        }
    }, [data]);

    const onDownloadData = async (
        dataType = CLICKED_BUTTON_MAP[clickedButton]
    ) => {
        setIsDropdownVisible(!isDropdownVisible);
        const filters = phraseDownloadDataFilter(generalFilters, timeZone);
        loadReportData(
            downloadOrganizationDataWithRawData,
            downloadOrganizationDataWithWeekly,
            downloadOrganizationDataWithAbundance,
            downloadSpeciesLineChart,
            getOrganizationSummaryData,
            dataType,
            filters
        );
    };

    const params = new URLSearchParams(window.location.search);
    const buttonType = params.get("button");
    const isChart = buttonType && buttonType.toLowerCase().includes("chart");

    const handleGenerateReport = () => {
        setShowReport(true);
        onDownloadData();
    };

    const handleDownloadClicked = () => {
        if (isChart) {
            handleDownloadChart(clickedButton);
        } else {
            handleDownloadReport(data, clickedButton, columnVisibilityModel);
        }
    }


    return (
        <div className="flex-auto">
            <ReportFilter
                isDropdownVisible={isDropdownVisible}
                setIsDropdownVisible={setIsDropdownVisible}
            />
            <div>
                <div style={{ marginTop: "5px" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "50px",
                            marginTop: "20px",
                            marginBottom: "20px",
                        }}
                    >
                        <Button
                            variant={"contained"}
                            color={"primary"}
                            style={{ fontSize: "12px" }}
                            onClick={handleGenerateReport}
                        >
                            Generate Chart
                        </Button>
                        <Button
                            variant={"contained"}
                            color={"primary"}
                            sx={{
                                display: clickedButton === "Pie-chart" ? "none" : "flex",
                                fontSize: "12px",
                            }}
                            disabled={!readyDownload}
                            onClick={() => handleDownloadClicked()}
                        >
                            {isChart ? "Download Chart" : "Download Report"}
                        </Button>
                    </div>
                    <hr
                        style={{
                            border: "none",
                            borderTop: "2px dotted #8e9eab",
                            margin: "5px 0",
                            height: "1px",
                        }}
                    />
                </div>
                <div
                    style={{
                        textAlign: "center",
                        fontWeight: "500",
                        color: "#3f51b5",
                        marginTop: "30px",
                        justifyContent: "center",
                        maxWidth: "100%",
                    }}
                >
                    <div
                        style={{
                            alignItems: "center",
                            maxWidth: "95vw",
                        }}
                        id="chart-container"
                    >
                        {showReport &&
                            <ReportsLayout
                                clickedButton={clickedButton}
                                rawData={rawData}
                                onDownloadData={onDownloadData}
                                columnVisibilityModel={columnVisibilityModel}
                                setColumnVisibilityModel={setColumnVisibilityModel}
                                loading={loading}
                                timeZone={timeZone}
                            />}
                        {loading && <LoadingCircularProgress />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const mapStateToProps = (state) => {
    const { selectedDevice, ...devices } = state.devices;
    const { timeZone } = state.user;
    const curDevices = Object.values(
        _.pickBy(
            devices,
            (value, _) => value?.env === process.env.REACT_APP_ENV.toUpperCase()
        )
    );
    const generalFilters = {
        ...state.data.generalFilters,
        algThreshold: state.user.algConfidence
            ? state.user.algConfidence
            : DEFAULT_ALG_CONFIDENCE_THRESHOLDS.default,
    };

    return {
        devices: curDevices,
        selectedDevice,
        timeZone,
        generalFilters: generalFilters,
        data: state.report?.report?.data || [],
        loading: state.report?.report?.loading || false,
    };
};

export default connect(
    mapStateToProps,
    // mapDispatchToProps,
    {
        fetchOrganizationFilters,
        updateGeneralFilters,
        clearGeneralFilters,
        loadReportData,
        clearReportData,
    }
)(Reports);
